import { Link } from "react-router-dom";
import {
    ArrowLeft,
    ShieldCheck,
    Sparkles,
    HeartHandshake,
    Droplets,
    Award,
    MapPin,
    Users,
    Clock,
} from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";

const HERO_IMG =
    "https://images.pexels.com/photos/17370583/pexels-photo-17370583.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const POLISH_IMG =
    "https://images.unsplash.com/photo-1750492960768-b869a06066dc?fm=jpg&q=70&w=1200&auto=format&fit=crop";
const INTERIOR_IMG =
    "https://images.pexels.com/photos/29566884/pexels-photo-29566884.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";

const VALUES = [
    {
        icon: Sparkles,
        title: "Obsessed with Detail",
        body: "Swirl-free finishes. Lint-free interiors. We chase the 1% that most shops skip.",
    },
    {
        icon: ShieldCheck,
        title: "Safe Products Only",
        body: "Paint-safe chemicals, pH-neutral soaps, microfiber-only contact. No shortcuts that ruin clear coat.",
    },
    {
        icon: HeartHandshake,
        title: "Honest Pricing",
        body: "Clear packages, clear add-ons. No surprise line items at pickup.",
    },
    {
        icon: Droplets,
        title: "Water-Conscious",
        body: "Efficient rinse systems designed for Arizona — premium results, minimal waste.",
    },
];

const STATS = [
    { label: "Vehicles Detailed", value: "500+" },
    { label: "Years in Business", value: "4+" },
    { label: "Client Rating", value: "4.9★" },
    { label: "Repeat Clients", value: "72%" },
];

export default function About() {
    return (
        <div data-testid="about-page" className="min-h-screen bg-ac-bg text-white">
            <Navbar />

            {/* Hero */}
            <section
                data-testid="about-hero"
                className="relative pt-40 pb-24 md:pt-48 md:pb-32 px-6 md:px-12 overflow-hidden"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-50"
                    style={{ backgroundImage: `url(${HERO_IMG})` }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/70 to-[#0A0A0A]" />
                <div
                    className="absolute right-0 top-0 w-[500px] h-[500px] rounded-full blur-[120px] opacity-40"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(157,76,221,0.55), transparent 60%)",
                    }}
                />

                <div className="relative z-10 max-w-7xl mx-auto">
                    <Link
                        to="/"
                        data-testid="about-back-home-link"
                        className="inline-flex items-center gap-2 font-heading text-[11px] uppercase tracking-[0.28em] text-white/60 hover:text-[#9D4CDD] transition-colors mb-8"
                    >
                        <ArrowLeft size={14} /> Back Home
                    </Link>
                    <div className="max-w-3xl">
                        <div className="font-heading text-xs uppercase tracking-[0.32em] text-[#9D4CDD] font-bold mb-4">
                            / About AC Detailing
                        </div>
                        <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter text-white leading-[0.95]">
                            Detailing is a craft.{" "}
                            <span className="text-[#9D4CDD]">We treat it like one.</span>
                        </h1>
                        <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
                            A family-run mobile detailing team born in Surprise,
                            Arizona — where the sun bakes paint, dust gets into
                            everything, and every vehicle deserves a fighting
                            chance to look its best.
                        </p>
                    </div>
                </div>
            </section>

            {/* Story */}
            <section className="relative py-20 md:py-24 px-6 md:px-12">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-5 space-y-6">
                        <div className="relative aspect-square overflow-hidden">
                            <img
                                src={POLISH_IMG}
                                alt="Polishing a vehicle"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                        <div className="relative aspect-[4/3] overflow-hidden hidden lg:block">
                            <img
                                src={INTERIOR_IMG}
                                alt="Luxury car interior"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="font-heading text-xs uppercase tracking-[0.32em] text-[#9D4CDD] font-bold mb-4">
                            / Our Story
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-white">
                            From a driveway in Surprise to 500+ spotless rides.
                        </h2>
                        <div className="mt-8 space-y-5 text-white/70 leading-relaxed">
                            <p>
                                AC Detailing started the way most great small
                                businesses do — with a buddy who needed his truck
                                cleaned, a weekend with nothing to do, and a promise
                                that turned into a reputation. What began as side
                                work grew into a full-time mobile detailing
                                operation serving Surprise, Sun City West, El
                                Mirage, Waddell, Peoria, and Goodyear.
                            </p>
                            <p>
                                We live here. We know what Arizona does to a vehicle
                                — the sun fading dashboards, the dust eating into
                                crevices, the monsoon hard-water spots baked onto
                                glass. We built our service menu specifically for
                                this climate, using premium products that are safe
                                for paint and tough on desert grime.
                            </p>
                            <blockquote className="pl-5 border-l-2 border-[#9D4CDD] italic text-white/85 text-lg">
                                &ldquo;The way we see it: your car is a big
                                investment. A detail shouldn&apos;t feel like
                                another expense — it should feel like a reset.&rdquo;
                            </blockquote>
                            <p>
                                Every detail is performed by our own team — never
                                contracted out, never rushed. When we book a slot,
                                it&apos;s yours. No double-booking, no cutting
                                corners to squeeze in the next job.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section
                data-testid="about-stats"
                className="relative py-16 px-6 md:px-12 bg-[#121212] border-y border-white/5"
            >
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
                    {STATS.map((s) => (
                        <div key={s.label} className="text-center md:text-left">
                            <div className="font-heading text-5xl md:text-6xl font-black text-[#9D4CDD] tracking-tighter">
                                {s.value}
                            </div>
                            <div className="mt-2 font-heading text-[11px] uppercase tracking-[0.24em] text-white/50">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Values */}
            <section className="relative py-24 md:py-32 px-6 md:px-12">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-2xl mb-14">
                        <div className="font-heading text-xs uppercase tracking-[0.32em] text-[#9D4CDD] font-bold mb-4">
                            / What We Stand For
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-white">
                            Four values.{" "}
                            <span className="text-[#9D4CDD]">Zero compromises.</span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {VALUES.map(({ icon: Icon, title, body }) => (
                            <div
                                key={title}
                                className="bg-[#121212] border border-white/5 p-8 hover:border-[#9D4CDD]/30 transition-colors"
                            >
                                <div className="w-12 h-12 flex items-center justify-center border border-[#9D4CDD]/30 bg-[#9D4CDD]/10 mb-6">
                                    <Icon className="text-[#9D4CDD]" size={22} />
                                </div>
                                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white">
                                    {title}
                                </h3>
                                <p className="mt-3 text-white/60 leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process */}
            <section
                data-testid="about-process"
                className="relative py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A] border-t border-white/5"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-2xl mb-14">
                        <div className="font-heading text-xs uppercase tracking-[0.32em] text-[#9D4CDD] font-bold mb-4">
                            / The Process
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-white">
                            How a booking works.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                        {[
                            { icon: Users, title: "Reach Out", body: "Fill the contact form with your vehicle + preferred date." },
                            { icon: Clock, title: "Confirm Slot", body: "We respond within 24 hrs with pricing and a confirmed time." },
                            { icon: Droplets, title: "We Detail", body: "Our crew arrives with water, power, and premium products." },
                            { icon: Award, title: "Walk-Around", body: "We inspect together. Not happy? We fix it before we leave." },
                        ].map(({ icon: Icon, title, body }, idx) => (
                            <div
                                key={title}
                                className="relative bg-[#121212] border border-white/5 p-6"
                            >
                                <div className="font-heading text-7xl font-black text-[#9D4CDD]/15 absolute top-2 right-4 tracking-tighter">
                                    0{idx + 1}
                                </div>
                                <Icon className="text-[#9D4CDD] relative" size={22} />
                                <div className="mt-4 font-heading text-lg font-bold text-white">
                                    {title}
                                </div>
                                <div className="mt-2 text-sm text-white/55 leading-relaxed">
                                    {body}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative py-24 px-6 md:px-12">
                <div className="max-w-5xl mx-auto bg-[#121212] border border-[#9D4CDD]/30 p-10 md:p-16 text-center relative overflow-hidden">
                    <div
                        className="absolute -top-24 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[120px] opacity-40"
                        style={{
                            background:
                                "radial-gradient(circle, rgba(157,76,221,0.6), transparent 60%)",
                        }}
                    />
                    <div className="relative z-10">
                        <div className="inline-flex items-center gap-2 text-[#9D4CDD] font-heading text-xs uppercase tracking-[0.32em] font-bold mb-4">
                            <MapPin size={14} /> Surprise, AZ
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-white">
                            Ready to ride clean?
                        </h2>
                        <p className="mt-5 text-white/65 max-w-xl mx-auto leading-relaxed">
                            Book your detail now or send a message — we&apos;ll get
                            back within 24 hours.
                        </p>
                        <Link
                            to="/#contact"
                            data-testid="about-cta-book-link"
                            className="mt-8 inline-flex items-center gap-3 bg-[#9D4CDD] text-white px-8 py-4 font-heading text-[12px] font-bold tracking-[0.24em] uppercase hover:bg-[#B366F9] transition-all hover:shadow-[0_0_24px_rgba(157,76,221,0.45)]"
                        >
                            Book a Detail
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
